"use client";

import React, { useEffect, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { trackEvent, Events, Sources } from "@/app/helpers/analytics";
import { openWhatsAppWithTracking } from "@/app/helpers/trackingService";

import WeddingCard from "../../components/listing/template";
import { ListingTemplateModel } from "../../models/ListingTemplateModel";

// Custom hook for responsive styles
function useWindowSize() {
  // Initialize with mobile-first default to prevent layout shift
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 375, // Default to mobile size
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Get the actual window width
      setWindowSize({
        width: window.innerWidth,
      });
    }

    // Add event listener
    if (typeof window !== "undefined") {
      // Initialize with the current window width immediately
      handleResize();

      // Listen for future resize events
      window.addEventListener("resize", handleResize);

      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []); // Empty array ensures effect is only run on mount and unmount

  return windowSize;
}

interface WeddingGalleryContentProps {
  initialPagesFilter: number[];
  initialCultureFilter: string;
  initialTemplates: ListingTemplateModel[];
}

export function WeddingGalleryContent({
  initialPagesFilter,
  initialCultureFilter,
  initialTemplates,
}: WeddingGalleryContentProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { width } = useWindowSize();

  // Debug width - remove after testing
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("Actual window.innerWidth:", window.innerWidth);
      console.log("Hook reported width:", width);
    }
  }, [width]);

  const [templates, setTemplates] =
    useState<ListingTemplateModel[]>(initialTemplates);
  const [isLoading, setIsLoading] = useState(false); // Set to false since we have initial data
  const [error, setError] = useState<string | null>(null);

  // State for filters
  const [selectedPages, setSelectedPages] =
    useState<number[]>(initialPagesFilter);
  const [selectedCulture, setSelectedCulture] =
    useState<string>(initialCultureFilter);

  // Function to get responsive grid template columns based on screen width
  const getGridTemplateColumns = (templateCount: number) => {
    // Make sure we have a valid width
    // Default to mobile size to prevent layout shift
    if (typeof width !== "number" || width === 0) {
      return "repeat(auto-fit, minmax(140px, 1fr))"; // Default to mobile size
    }
    if (templateCount < 3 && width > 640) {
      console.log("width", width);
      return `repeat(${templateCount}, 250px)`;
    }
    if (templateCount == 3 && width > 640) {
      console.log("width", width);
      return `repeat(${templateCount}, 140px)`;
    }
    // console.log("Current width in getGridTemplateColumns:", width);

    if (width < 640) return "repeat(auto-fit, minmax(140px, 1fr))";
    if (width < 768) return "repeat(2, 1fr)"; // 2 columns on tablet
    if (width < 1024) return "repeat(3, 1fr)"; // 3 columns on small desktop
    return "repeat(4, 1fr)"; // 4 columns on large desktop
  };

  // Function to update URL with current filters
  const updateUrlWithFilters = (pages: number[]) => {
    const params = new URLSearchParams(searchParams.toString());

    // Update only pages filter in URL
    if (pages.length > 0) {
      params.set("pages", pages.join(","));
    } else {
      params.delete("pages");
    }

    // Culture is already in the path, no need to add it again
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // Function to fetch templates with filters
  const fetchTemplates = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query parameters for API request
      const queryParams = new URLSearchParams();
      if (selectedPages.length > 0) {
        queryParams.set("pages", selectedPages.join(","));
      }
      if (selectedCulture) {
        queryParams.set("culture", selectedCulture);
      }

      // Make API request
      const response = await fetch(`/api/templates?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      console.error("Error fetching templates:", err);
      //   setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Track whether this is the first render with initialTemplates
  const firstRenderRef = React.useRef(true);

  // Fetch templates when filters change, but skip first render since we have server data
  useEffect(() => {
    // Update URL to reflect current filters (always do this)
    updateUrlWithFilters(selectedPages);

    // Skip API call on first render since we already have initialTemplates
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    // On subsequent filter changes, fetch new data
    fetchTemplates();
  }, [selectedPages, selectedCulture]);

  // Handle page filter changes
  const handlePageFilterChange = (pages: number[]) => {
    setSelectedPages(pages);
  };

  // Handle culture filter changes (to be called from CultureCategories component)
  const handleCultureFilterChange = (culture: string) => {
    setSelectedCulture(culture);
  };

  // Handle card click to navigate to template detail page
  const handleCardClick = (template: ListingTemplateModel) => {
    const slug = template.title
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");
    const path = `/templates/${slug}/${template.id * 55}`;

    router.prefetch(path);
    router.push(path);
  };

  // Function to determine if a template is popular
  const getIsPopular = (index: number) => {
    return index < 3; // First 3 templates are popular
  };

  // WhatsApp button handler
  const handleOrderOnWhatsApp = () => {
    // Track WhatsApp click event
    trackEvent(Events.whatsappClick, {
      source: Sources.listingPage,
    });

    openWhatsAppWithTracking({
      customMessage: "Hi, I'm interested in ordering a wedding invitation. Could you please guide me through the process?",
    });
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Left sidebar with filters */}

        {/* Templates grid */}
        <div className="flex-1">
          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900"></div>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-red-500 bg-red-50 rounded-lg">
              <p>Error: {error}</p>
            </div>
          )}

          {templates && templates.length > 0 && (
            <div className="grid gap-4 sm:gap-8">
              <div
                className="grid gap-4 sm:gap-8 grid-cols-2"
                style={{
                  gridTemplateColumns: getGridTemplateColumns(templates.length),
                }}
              >
                {templates.map((template, index) => (
                  <WeddingCard
                    key={template.id || index}
                    template={template}
                    index={index}
                    isPopular={getIsPopular(index)}
                    onCardClick={handleCardClick}
                  />
                ))}
              </div>
            </div>
          )}

          {templates && templates.length === 0 && !isLoading && !error && (
            <div className="text-center py-8 text-gray-500">
              <p>No templates found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* WhatsApp Button - Floating */}
      <button
        onClick={handleOrderOnWhatsApp}
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:bg-[#20BA5A] transition-all duration-300 flex items-center justify-center gap-2 group"
        aria-label="Order on WhatsApp"
        style={{
          boxShadow: "0 4px 12px rgba(37, 211, 102, 0.4)",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-shrink-0"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
            fill="currentColor"
          />
        </svg>
        <span className="hidden md:block font-semibold text-sm whitespace-nowrap">
          Order on WhatsApp
        </span>
      </button>
    </div>
  );
}
